import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type { Campaign } from '../../../../bridge/types/campaign.ts';
import type { Process } from '../../../../bridge/types/process.ts';
import { CAMPAIGN_ID_PREFIX } from '../../../../bridge/utils/processClassification.ts';

import { humanize } from './strategyFormat';

export type MetricEntry = {
	value: number;
	label: string;
	format: 'percent' | 'count';
	raw?: Record<string, number>;
};

export type CampaignMetrics = Record<string, MetricEntry>;

export type CampaignMetricsMap = Record<string, CampaignMetrics>;

// A recurring campaign has a Process but no Campaign document — suggest_play
// writes only the process (shared/agents/tools/suggest_play.py), and
// propose_process is the sole writer of the campaigns container. So the
// Campaigns space would never show one. It reached the user through the Jobs
// list instead, which is wrong: a campaign produces content, and a job reports
// information. The Jobs list now excludes it, so this row is its only home, and
// the pause control on the campaign panel depends on it.
//
// `goal` reads `user_summary`, not `description`. The description is the agent's
// own task text, which is written for an agent and not for a person.
// `title` is humanized because suggest_play stores the play registry key there
// (`title=agent.name`, shared/agents/tools/suggest_play.py), so the stored value
// is a slug such as `instagram_post`. CampaignLibrary renders `title || goal`, so
// the slug would reach the name cell and `goal` would never be read. `goal` cannot
// stand in as the name either: it holds the standup format, not a name.
// `status` is mandatory in practice: CampaignLibrary normalizes a missing status
// to 'proposed', which would hide every recurring campaign behind the filters.
// It is also held back while the profile review is pending. suggest_play pauses
// the heartbeat schedule at creation so no content goes out before that review,
// then writes status="active" regardless, so the stored value promises a schedule
// that does not run yet. The hold is display-only on purpose: writing 'paused'
// would make POST /agents/resume-heartbeats skip the campaign for ever, because
// that route reads a paused Process as the owner's own choice.
const recurringCampaignRow = (
	process: Process,
	profileReviewPending: boolean,
): Campaign => ({
	id: process.id,
	user_id: process.user_id,
	title: humanize(process.title),
	goal: process.user_summary ?? '',
	artifact_ids: process.artifact_ids ?? [],
	action_ids: process.action_ids ?? [],
	process_ids: [process.id],
	status:
		profileReviewPending && process.status === 'active'
			? 'proposed'
			: process.status,
	created_at: process.created_at,
	updated_at: process.updated_at,
});

export const useCampaigns = () => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const bridgeApi = useBridgeApi();
	// The same pair NavBar reads to decide the review is still pending, so the two
	// agree by construction. Both flags must be present: a customer who predates
	// the flag has neither, and treating that as "pending" would label a campaign
	// that really does run as proposed.
	const onboarding = bridgeApi.user_state?.onboarding_status;
	const profileReviewPending =
		onboarding?.business_profile_ready === true &&
		onboarding?.business_profile_reviewed !== true;
	const [campaigns, setCampaigns] = useState<Campaign[]>([]);
	const [metrics, setMetrics] = useState<CampaignMetricsMap>({});
	const [loading, setLoading] = useState(true);
	// A failed metrics fetch used to become an empty map, which renders exactly like a
	// campaign that has no results yet — so a 500 looked like "no data". Callers need to
	// tell those apart, so the failure is recorded as user-ready copy instead.
	const [metricsError, setMetricsError] = useState<string | null>(null);

	const refreshRef = useRef<() => Promise<void>>(async () => {});

	const refresh = useCallback(async () => {
		if (!userId) return;
		try {
			let metricsFailed = false;
			const [campaignsRes, metricsRes, recurringRes] = await Promise.all([
				bridgewayApi
					.get<Campaign[]>(`/api/user/${userId}/campaigns/`)
					.catch(() => [] as Campaign[]),
				bridgewayApi
					.get<CampaignMetricsMap>(
						`/api/user/${userId}/processes/campaign-metrics`,
					)
					.catch((err) => {
						logger.error('Failed to fetch campaign metrics', err);
						metricsFailed = true;
						return {} as CampaignMetricsMap;
					}),
				bridgewayApi
					.get<Process[]>(`/api/user/${userId}/processes/?kind=campaign`)
					.catch((err) => {
						logger.error('Failed to fetch recurring campaigns', err);
						return [] as Process[];
					}),
			]);
			// The prefix is checked here, not trusted from the response. An older
			// beacon ignores an unknown `kind` and answers with EVERY process,
			// so without this the Campaigns space shows the upkeep job, the
			// seeded jobs and every monitor as campaigns. A current beacon
			// already returns only these ids, so the filter costs it nothing.
			setCampaigns([
				...(Array.isArray(campaignsRes) ? campaignsRes : []),
				...(Array.isArray(recurringRes)
					? recurringRes
							.filter(
								(p) =>
									typeof p.id === 'string' &&
									p.id.startsWith(CAMPAIGN_ID_PREFIX),
							)
							.map((p) => recurringCampaignRow(p, profileReviewPending))
					: []),
			]);
			if (metricsFailed) {
				// Leave any metrics already on screen in place. On a failed refresh, stale
				// numbers alongside this message beat blanking the whole results column.
				setMetricsError('We couldn’t load your results.');
			} else {
				setMetrics(
					metricsRes && typeof metricsRes === 'object' ? metricsRes : {},
				);
				setMetricsError(null);
			}
		} catch (err) {
			logger.error('useCampaigns refresh failed', err);
		} finally {
			setLoading(false);
		}
	}, [userId, bridgewayApi, profileReviewPending]);

	refreshRef.current = refresh;

	// profileReviewPending is a dependency because the rows carry the hold. It flips
	// once per customer, and without a re-map the campaign keeps saying 'proposed'
	// after the review already released its schedule.
	useEffect(() => {
		if (!userId) return undefined;
		void refreshRef.current();
	}, [userId, profileReviewPending]);

	return { campaigns, metrics, loading, metricsError, refresh };
};

export const useCampaignMetrics = (
	processId: string | null | undefined,
): {
	metrics: CampaignMetrics | undefined;
	loading: boolean;
	metricsError: string | null;
} => {
	const { campaigns, metrics, loading, metricsError } = useCampaigns();
	// Every path returns metricsError: an undefined `metrics` alone can't say whether the
	// campaign has no results or the request failed, and a caller that never sees the
	// error silently goes back to showing nothing.
	if (!processId) return { metrics: undefined, loading, metricsError };
	const campaign = campaigns.find((c) =>
		(c.process_ids ?? []).includes(processId),
	);
	if (!campaign) return { metrics: undefined, loading, metricsError };
	return { metrics: metrics[campaign.id], loading, metricsError };
};
